import { isUUID } from "validator";
import { crudQuery, crudRead, crudUpdate, crudDelete, crudDescription, crudCreate, crudGet, crudPost } from "./request";
import {QueryFilter} from "@nestjsx/crud-request";

export const errorObject: Record<string, string> = {
	'PROFESSIONAL_NOT_AVAILABLE': 'Profissional indisponível para dia e horário escolhido.',
	'INVALID_CHECK_IN_OUT_DATE_TIMES': 'Data/hora de check-in ou check-out inválidas',
}

export class CrudModel {
	public endpoint: string;
	public description: any;
	public additionalDescription: any = [];

	constructor(endpoint: string, additionalDescription: any[] = []) {
		this.endpoint = endpoint;
		this.additionalDescription = additionalDescription;
	}

	async getDescription() {
		if (!this.description) {
			const backendDescription = await crudDescription(this.endpoint);
			const mergedDescription = [...backendDescription];

			this.additionalDescription.forEach((frontendField: any) => {
				const existingIndex = mergedDescription.findIndex((field: any) => field.key === frontendField.key);
				if (existingIndex >= 0) {
					mergedDescription[existingIndex] = { ...mergedDescription[existingIndex], ...frontendField };
				} else {
					mergedDescription.push(frontendField);
				}
			});

			this.description = mergedDescription;
		}
		return this.description;
	}

	async create(data: any) {
		try {
			return (await crudCreate(this.endpoint, data)); // await before return allows error catching
		} catch (error) {
			await this.handleError(error);
		}
	}

	async search(queryParameters: any, joinAll = false, joinKey: string | null = null, joinBlacklist: string[] = []) {
		if (joinAll) {
			queryParameters.join = await this.getJoinableFields();
		} else if (joinKey) {
			const description = await this.getDescription();
			queryParameters.join = description
				.filter((field: any) => field.kind === "relation" && field.key.includes(joinKey) && !joinBlacklist.find(blacklistedKey => field.key.includes(blacklistedKey)))
				.map((field: any) => ({ field: field.key }));
		}
		return crudQuery(this.endpoint, queryParameters);
	}

	async read(id: string | number, joinKey: string | null = null, joinBlacklist: string[] = [], filters: Array<QueryFilter> = []) {
		const queryParameters: { join?: any[]; filter?: Array<QueryFilter> } = { filter: filters };
		if (joinKey) {
			const description = await this.getDescription();
			queryParameters.join = description
				.filter((field: any) => field.kind === "relation" && field.key.includes(joinKey) && !joinBlacklist.find(blacklistedKey => field.key.includes(blacklistedKey)))
				.map((field: any) => ({ field: field.key }));
		} else {
			queryParameters.join = await this.getJoinableFields();
		}
		return crudRead(this.endpoint, id, queryParameters);
	}

	async update(id: string | number, data: any) {
		const payload = { ...data };
		const description = await this.getDescription();
		description.forEach((field: any) => {
			if (field.kind === "file" && payload[field.key] && !isUUID(payload[field.key])) {
				delete payload[field.key];
			}
		});
		try {
			return (await crudUpdate(this.endpoint, id, payload)); // await before return allows error catching
		} catch (error) {
			await this.handleError(error);
		}
	}

	async delete(id: string | number, auditNotes: string) {
		return crudDelete(this.endpoint, id, auditNotes);
	}

	async executeAction(id: string | number | null, action: string, data?: any, blob = false) {
		return crudPost(this.endpoint, `${id ? id + '/' : ''}${action}`, data, blob);
	}

	async get(path: string, params?: any) {
		return crudGet(this.endpoint, path, params);
	}

	private async getJoinableFields(): Promise<{field: string}[]> {
		const description = await this.getDescription();
		let relationFields = description.filter((field: any) => field.kind === "relation");
		// remove one-to-many and many-to-many relations
		const toManyRelationsKeys = relationFields.filter((field: any) => ["one-to-many", "many-to-many"].includes(field.relationType)).map((field: any) => field.key);
		toManyRelationsKeys.forEach((key: string) => {
			relationFields = relationFields.filter((field: any) => !field.key.includes(key));
		});
		return relationFields.map((field: any) => ({ field: field.key }));
	}

	async handleError(error: any) {
		const description = await this.getDescription();
		switch (error.statusCode) {
			case 400: {
				console.log({ errorMessage: error.message })
				if (error?.message && typeof error.message !== 'string') {
					const flatErrors = flattenValidationErrors(error.message);
					error.flatErrors = flatErrors;

					const propertyKeys: string[] = flatErrors.filter(error => error.constraints).map(error => error.property);
					const propertyNames = propertyKeys.map(key => description.find((field: any) => field.key === key)?.name || description.find((field: any) => field.key.includes(key))?.name || key);
					error.friendlyMessage = `Os seguintes campos possuem erros:<br>${propertyNames.join(', ')}`;
				} else if (error?.message) {
					error.friendlyMessage = this.mapErrorToFriendlyMessage(error.message);
				}
			}
			break;
		}
		throw error;
	}

	private mapErrorToFriendlyMessage(errorMessage: string): string {
		return errorObject[errorMessage]
	}
}

export class ValidationError {
	property: string;
	target: any;
	value: any;
	children?: ValidationError[];
	constraints?: any;
}

function flattenValidationErrors(errors: any, prefix: string = ''): { property: string, constraints: any}[] {
	let flattenedErrors: any[] = [];
	errors.forEach((error: any) => {
		const errorProperty = prefix ? `${prefix}.${error.property}` : error.property;
		flattenedErrors.push({
			property: errorProperty,
			constraints: error.constraints,
		});
		if (error.children) {
			flattenedErrors = flattenedErrors.concat(flattenValidationErrors(error.children, errorProperty));
		}
	});
	return flattenedErrors;
}
